import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import { Link } from "gatsby-link";
import React, { Fragment, useEffect, useState } from "react";
import * as Yup from "yup";
import bg from "../assets/backgrounds/demo_modal_bg.png";
import {
  BestSupportIcon,
  HighPerformerIcon,
  LeaderAmericasIcon,
  LeaderIcon,
} from "../assets/icons/DemoModalIcons";
import {
  BookDemoIcon,
  CloseModal,
  SuccessIcon,
} from "../assets/icons/ModalIcons";
import {
  HUBSPOT_PROXY_URL,
  blockedDomains,
  consentText,
  emailRegex,
} from "../constants";
import "../styles/additional-styles/DemoModal.scss";
import {
  CONSENT_AGREEMENT,
  USE_CASE_TEXT_VALIDATION,
  WORK_EMAIL_VALIDATION,
  isDomainBlocked,
  isEducationalEmail,
} from "../utilities/helpers";
import LogoMarquee from "./LogoMarquee";
import { useLocation } from "@reach/router";

const SingleEmailFormFormSchema = Yup.object().shape({
  email: WORK_EMAIL_VALIDATION,
});

const RequestTrialSchema = Yup.object().shape({
  workEmail: WORK_EMAIL_VALIDATION,
  useCaseFreeTextForm: USE_CASE_TEXT_VALIDATION,
  consent_to_communicate_checkbox: CONSENT_AGREEMENT,
});

const logo = {
  id: "trusted_brands_list$028741ec-378d-47ef-b7ee-982a9c84a3c4",
  slice_type: "trusted_brands_list",
  items: [
    {
      brand_image: {
        alt: null,
        url: "https://encord.cdn.prismic.io/encord/388e727c-2352-4235-b39a-af0d5451a6b8_VizAi.svg?fit=max",
      },
    },
    {
      brand_image: {
        alt: null,
        url: "https://encord.cdn.prismic.io/encord/a1049d65-4055-42b6-9de1-5b44e9f59113_NHS.svg?fit=max",
      },
    },
    {
      brand_image: {
        alt: null,
        url: "https://encord.cdn.prismic.io/encord/a23c2f49-922b-41c7-bf7a-9e48a254481b_UHN.svg?fit=max",
      },
    },
    {
      brand_image: {
        alt: null,
        url: "https://encord.cdn.prismic.io/encord/aadd2948-c3a5-4f4f-9ec5-482b3153058f_stanford.svg?fit=max",
      },
    },
    {
      brand_image: {
        alt: null,
        url: "https://encord.cdn.prismic.io/encord/9d3aa783-1689-436b-9069-939f1ee05079_IterativeHealth.svg?fit=max",
      },
    },
    {
      brand_image: {
        alt: null,
        url: "https://encord.cdn.prismic.io/encord/09a106bc-1728-4460-b6a5-ffedd4ec3ecf_MGH.svg?fit=max",
      },
    },
  ],
  primary: {
    label: {
      html: "<p>Trusted by pioneering AI teams</p>",
      text: "Trusted by pioneering AI teams",
    },
  },
};

const SingleEmailForm = ({ handleNext, title, closeModal }) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (values, { resetForm }) => {
    console.log("resetForm", resetForm);

    const [cookie] = document.cookie
      .split("; ")
      .filter((x) => x.includes("hubspotutk="));
    const [, hubspotutk] = cookie?.split("=") || ["", ""];
    setError("");

    try {
      if (!isDomainBlocked(values.email)) {
        setLoading(true);
        const requestPayload = {
          fields: [
            {
              objectTypeId: "0-1",
              name: "email",
              value: values?.email,
            },
          ],
          context: {
            pageUri: location?.href,
            pageName: location?.pathname,
          },
        };

        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = {
          utm_medium: urlParams.get("utm_medium") || "",
          utm_source: urlParams.get("utm_source") || "",
          utm_campaign: urlParams.get("utm_campaign") || "",
          utm_content: urlParams.get("utm_content") || "",
          utm_term: urlParams.get("utm_term") || "",
        };
        requestPayload.fields.push(
          ...Object.entries(utmParams).map(([key, value]) => ({
            objectTypeId: "0-1",
            name: key,
            value: value,
          }))
        );

        if (hubspotutk) {
          requestPayload.context.hutk = hubspotutk;
        }

        await axios.post(
          HUBSPOT_PROXY_URL,
          Object.assign(requestPayload, {
            portalId: "25381551",
            formId: "c90a5ae1-1852-43f5-9346-a57837b9f470",
          })
        );

        resetForm();
        localStorage.setItem("workEmail", values?.email);
        handleNext();
      }
    } catch (error) {
      console.log("error while submitting form");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog.Panel className=" relative w-full max-w-xl transform overflow-hidden rounded-t-[20px] lg:rounded-t-[0px] lg:!rounded-l-[20px] bg-white px-5 py-10 sm:px-9 sm:py-9 text-left align-middle">
      <div className="flex items-center justify-between mt-5 sm:mt-0">
        <div className="flex items-center gap-x-2">
          <BookDemoIcon />
          <Dialog.Title
            as="h3"
            className="text-2xl font-semibold font-manrope tracking-tighter_1 text-purple-1100"
          >
            {title}
          </Dialog.Title>
        </div>
      </div>
      <p className="text-xs sm:text-sm text-gray-1200 tracking-[-0.3px] sm:tracking-[-0.7px] mt-1.5 sm:mt-2.5">
        To get started with Encord, schedule a demo with one of our AI
        specialists today.
      </p>
      <span
        role="button"
        tabIndex={0}
        onClick={closeModal}
        className="cursor-pointer absolute right-6 top-6 !mt-0 block lg:hidden"
      >
        <CloseModal />
      </span>
      <div className="mt-6">
        <Formik
          initialValues={{
            email: "",
          }}
          validationSchema={SingleEmailFormFormSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            values,
            setTouched,
            validateForm,
            setFieldValue,
            form,
            isValid,
            dirty,
            ...rest
          }) => {
            return (
              <Form
                className={`${isEducationalEmail(values?.workEmail) ? "academic-form" : "commercial-form"}`}
              >
                <section className="flex flex-col sm:gap-y-2.5 gap-y-2">
                  <div className="flex flex-col flex-1 w-full">
                    <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-xs font-medium mb-1.5">
                      Work email
                      <sup className="text-red-500">*</sup>
                    </label>
                    <Field
                      name="email"
                      placeholder="Enter your work email"
                      type="text"
                      className="text-gray-2700 focus:border-[#0958D9] placeholder:text-gray-2200 text-sm rounded-lg placeholder-gray-1600 border-gray-2200 border py-2.5 px-5"
                    />
                    {errors.workEmail && touched.workEmail ? (
                      <small className="text-red-500">{errors.workEmail}</small>
                    ) : null}
                  </div>

                  {error && <small className="text-red-500">{error}</small>}

                  <div className="flex w-full gap-5">
                    <button
                      type={Object.keys(errors)?.length ? "button" : "submit"}
                      disabled={loading || !(isValid && dirty)}
                      className="w-full py-2 mx-auto mt-2 mb-5 text-base font-semibold text-white rounded-full shadow-lg bg-purple-1500 hover:bg-purple-400 px-7 sm:text-lg sm:mt-4 tracking-tighter_1 disabled:bg-purple-400 disabled:cursor-not-allowed"
                      {...rest}
                    >
                      {loading ? (
                        <div role="status">
                          <div className="flex gap-x-2.5 justify-center items-center">
                            <span>Submitting</span>
                            <span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="19"
                                viewBox="0 0 18 19"
                                role="presentation"
                                fill="none"
                                className="inline animate-spin"
                              >
                                <g clip-path="url(#clip0_88_6703)">
                                  <path
                                    d="M9 18.5C7.78535 18.5 6.60586 18.2627 5.49668 17.7934C4.42441 17.3398 3.46289 16.6895 2.63672 15.8633C1.81055 15.0371 1.16016 14.0756 0.706641 13.0033C0.237305 11.8941 0 10.7146 0 9.5C0 9.1502 0.283008 8.86719 0.632812 8.86719C0.982617 8.86719 1.26562 9.1502 1.26562 9.5C1.26562 10.5441 1.46953 11.5566 1.87383 12.5111C2.26406 13.4322 2.82129 14.2602 3.53145 14.9703C4.2416 15.6805 5.06953 16.2395 5.99062 16.6279C6.94336 17.0305 7.95586 17.2344 9 17.2344C10.0441 17.2344 11.0566 17.0305 12.0111 16.6262C12.9322 16.2359 13.7602 15.6787 14.4703 14.9686C15.1805 14.2584 15.7395 13.4305 16.1279 12.5094C16.5305 11.5566 16.7344 10.5441 16.7344 9.5C16.7344 8.45586 16.5305 7.44336 16.1262 6.48887C15.7373 5.56999 15.1744 4.73494 14.4686 4.02969C13.7641 3.3229 12.9288 2.75991 12.0094 2.37207C11.0566 1.96953 10.0441 1.76562 9 1.76562C8.6502 1.76562 8.36719 1.48262 8.36719 1.13281C8.36719 0.783008 8.6502 0.5 9 0.5C10.2146 0.5 11.3941 0.737305 12.5033 1.20664C13.5756 1.66016 14.5371 2.31055 15.3633 3.13672C16.1895 3.96289 16.8381 4.92617 17.2916 5.99668C17.7609 7.10586 17.9982 8.28535 17.9982 9.5C17.9982 10.7146 17.7609 11.8941 17.2916 13.0033C16.8398 14.0756 16.1895 15.0371 15.3633 15.8633C14.5371 16.6895 13.5738 17.3381 12.5033 17.7916C11.3941 18.2627 10.2146 18.5 9 18.5Z"
                                    fill="white"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_88_6703">
                                    <rect
                                      width="18"
                                      height="18"
                                      fill="white"
                                      transform="translate(0 0.5)"
                                    />
                                  </clipPath>
                                </defs>
                              </svg>
                              <span class="sr-only">Loading...</span>
                            </span>
                          </div>
                        </div>
                      ) : (
                        "Continue"
                      )}
                    </button>
                  </div>
                </section>
              </Form>
            );
          }}
        </Formik>

        <p className="text-xs text-center text-gray-1200">
          By pressing "continue", you agree to Encord's{" "}
          <Link
            to="/terms-and-conditions-uk"
            target="_blank"
            className="underline"
            aria-label="opens in a new tab"
          >
            terms and conditions
          </Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" className="underline">
            privacy policy
          </Link>
          .
        </p>
      </div>
    </Dialog.Panel>
  );
};

const DemoForm = ({ handleNext, title, closeModal, email, setIsSuccess }) => {
  const locationHook = useLocation();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const workEmail = localStorage.getItem("workEmail");
  const lidar = locationHook?.pathname === "/lidar/";
  const handleSubmit = async (values, { resetForm }) => {
    const [cookie] = document.cookie
      .split("; ")
      .filter((x) => x.includes("hubspotutk="));
    const [, hubspotutk] = cookie?.split("=") || ["", ""];
    setError("");
    const formId = isEducationalEmail(values?.workEmail)
      ? "41cdbcf3-f0ec-4e0d-9ede-8f72f2b9b07c"
      : lidar
        ? "747e99a5-c00e-433f-b258-962073c47fe2"
        : "b1f2fd0e-9b34-4a84-bf8d-c2ad4b28fd3e";
    try {
      if (
        emailRegex.test(values?.workEmail) &&
        !isDomainBlocked(values?.workEmail)
      ) {
        setLoading(true);

        if (
          // response?.data?.deliverable_email &&
          // response?.data?.mx &&
          // !response?.data?.disposable
          true
        ) {
          const requestPayload = {
            fields: [
              {
                objectTypeId: "0-1",
                name: "email",
                value: values?.workEmail,
              },

              {
                objectTypeId: "0-1",
                name: "use_case_free_text_form",
                value: values?.useCaseFreeTextForm,
              },
              {
                objectTypeId: "0-1",
                name: "consent_to_communicate_checkbox",
                value: values?.consent_to_communicate_checkbox,
              },
            ].filter((x) => x.value),
            context: {
              pageUri: location?.href,
              pageName: location?.pathname,
            },
          };

          // Add UTM parameters only for specific form IDs

          const urlParams = new URLSearchParams(window.location.search);
          const utmParams = {
            utm_medium: urlParams.get("utm_medium") || "",
            utm_source: urlParams.get("utm_source") || "",
            utm_campaign: urlParams.get("utm_campaign") || "",
            utm_content: urlParams.get("utm_content") || "",
            utm_term: urlParams.get("utm_term") || "",
          };
          requestPayload.fields.push(
            ...Object.entries(utmParams).map(([key, value]) => ({
              objectTypeId: "0-1",
              name: key,
              value: value,
            }))
          );

          if (hubspotutk) {
            requestPayload.context.hutk = hubspotutk;
          }

          await axios.post(
            HUBSPOT_PROXY_URL,
            Object.assign(requestPayload, {
              portalId: "25381551",
              formId: formId,
            })
          );
          /**
           * Append custom properties to Heap
           */
          if (window.heap) {
            window.heap.addUserProperties({
              marketing_email: values?.workEmail,
              use_case: values?.useCaseFreeTextForm,
            });
          }
          if (
            location.pathname !== "/lidar/" &&
            window?.hero &&
            !isEducationalEmail(values?.workEmail)
          ) {
            const _res = await window?.hero?.submit({
              email: values?.workEmail,
              use_case: values?.useCaseFreeTextForm,
            });

            _res && (await window?.hero?.dialog?.open(_res));
          }
          resetForm();
          localStorage.removeItem("workEmail");
          handleNext();
          closeModal();
        } else {
          setError("Please enter your work e-mail.");
        }
      } else {
        setError("Email must be formatted correctly.");
      }
    } catch (error) {
      console.log("error", error);
      if (
        error?.response?.data &&
        error?.response?.data.errors &&
        error?.response?.data.errors[0]?.message
      ) {
        const [, errorMessage] =
          error?.response?.data?.errors[0]?.message.split(". ") || ["", ""];
        if (errorMessage) {
          setError(errorMessage ?? error?.response?.data?.errors[0]?.message);
        }
      } else {
        setError("SERVER SIDE ERROR");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Panel className=" relative w-full max-w-xl transform overflow-hidden rounded-t-[20px] lg:rounded-t-[0px] lg:!rounded-l-[20px] bg-white px-5 py-10 sm:px-9 sm:py-9 text-left align-middle">
      <div className="flex items-center justify-between mt-5 sm:mt-0">
        <div className="flex items-center gap-x-2">
          <BookDemoIcon />
          <Dialog.Title
            as="h3"
            className="text-2xl font-semibold font-manrope tracking-tighter_1 text-purple-1100"
          >
            {title}
          </Dialog.Title>
        </div>
      </div>
      <p className="text-xs sm:text-sm text-gray-1200 tracking-[-0.3px] sm:tracking-[-0.7px] mt-1.5 sm:mt-2.5">
        {lidar
          ? "Sign up to get notified about our Physical AI suite availability."
          : `To get started with Encord, schedule a demo with one of our AI
        specialists today.`}
      </p>
      <span
        role="button"
        tabIndex={0}
        onClick={closeModal}
        className="cursor-pointer absolute right-6 top-6 !mt-0 block lg:hidden"
      >
        <CloseModal />
      </span>
      <div className="mt-6">
        <Formik
          initialValues={{
            workEmail: email || workEmail || "",
            useCaseFreeTextForm: "",
            consent_to_communicate_checkbox: false,
          }}
          validationSchema={RequestTrialSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            values,
            setTouched,
            validateForm,
            setFieldValue,
            form,
            isValid,
            dirty,
            ...rest
          }) => {
            return (
              <Form
                className={`${isEducationalEmail(values?.workEmail) ? "academic-form" : "commercial-form"}`}
              >
                <section className="flex flex-col sm:gap-y-2.5 gap-y-2">
                  <div className="flex flex-col flex-1 w-full">
                    <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-xs font-medium mb-1.5">
                      Work email
                      <sup className="text-red-500">*</sup>
                    </label>
                    <Field
                      name="workEmail"
                      placeholder="Enter your work email"
                      type="text"
                      className="text-gray-2700 focus:border-[#0958D9] placeholder:text-gray-2200 text-sm rounded-lg placeholder-gray-1600 border-gray-2200 border py-2.5 px-5"
                    />
                    {errors.workEmail && touched.workEmail ? (
                      <small className="text-red-500">{errors.workEmail}</small>
                    ) : null}
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-xs font-medium mb-1.5">
                      What problems are you looking to solve?{" "}
                      <sup className="text-red-500">*</sup>
                    </label>
                    <Field
                      as="textarea"
                      name="useCaseFreeTextForm"
                      placeholder="Tell us more about what you're trying to solve"
                      className="text-gray-2700 placeholder:text-gray-2200 text-sm rounded-lg placeholder-gray-1600 border-gray-2200 border min-h-[96px] py-2.5 px-5"
                    />
                    {errors.useCaseFreeTextForm &&
                    touched.useCaseFreeTextForm ? (
                      <small className="text-red-500">
                        {errors.useCaseFreeTextForm}
                      </small>
                    ) : null}
                  </div>

                  {error && <small className="text-red-500">{error}</small>}
                  <div className="mt-4 gap-2 flex sm:w-[477px] flex-col">
                    <label className="flex items-start gap-2 text-xs font-normal consent-required-histerik text-gray-1200">
                      <Field
                        className="mt-[1px] appearance-none w-4 h-4 border cursor-pointer border-gray-300 checked:bg-purple-1500 checked:border-none "
                        type="checkbox"
                        name="consent_to_communicate_checkbox"
                        // required
                      />
                      <div
                        dangerouslySetInnerHTML={{
                          __html: consentText?.marketingConsent,
                        }}
                      />
                    </label>
                  </div>
                  <div className="flex w-full gap-5">
                    <button
                      type={Object.keys(errors)?.length ? "button" : "submit"}
                      disabled={loading || !(isValid && dirty)}
                      className="w-full py-2 mx-auto mt-2 mb-5 text-base font-semibold text-white rounded-full shadow-lg bg-purple-1500 hover:bg-purple-400 px-7 sm:text-lg sm:mt-4 tracking-tighter_1 disabled:bg-purple-400 disabled:cursor-not-allowed"
                      {...rest}
                    >
                      {loading ? (
                        <div role="status">
                          <div className="flex gap-x-2.5 justify-center items-center">
                            <span>Submitting</span>
                            <span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="19"
                                viewBox="0 0 18 19"
                                role="presentation"
                                fill="none"
                                className="inline animate-spin"
                              >
                                <g clip-path="url(#clip0_88_6703)">
                                  <path
                                    d="M9 18.5C7.78535 18.5 6.60586 18.2627 5.49668 17.7934C4.42441 17.3398 3.46289 16.6895 2.63672 15.8633C1.81055 15.0371 1.16016 14.0756 0.706641 13.0033C0.237305 11.8941 0 10.7146 0 9.5C0 9.1502 0.283008 8.86719 0.632812 8.86719C0.982617 8.86719 1.26562 9.1502 1.26562 9.5C1.26562 10.5441 1.46953 11.5566 1.87383 12.5111C2.26406 13.4322 2.82129 14.2602 3.53145 14.9703C4.2416 15.6805 5.06953 16.2395 5.99062 16.6279C6.94336 17.0305 7.95586 17.2344 9 17.2344C10.0441 17.2344 11.0566 17.0305 12.0111 16.6262C12.9322 16.2359 13.7602 15.6787 14.4703 14.9686C15.1805 14.2584 15.7395 13.4305 16.1279 12.5094C16.5305 11.5566 16.7344 10.5441 16.7344 9.5C16.7344 8.45586 16.5305 7.44336 16.1262 6.48887C15.7373 5.56999 15.1744 4.73494 14.4686 4.02969C13.7641 3.3229 12.9288 2.75991 12.0094 2.37207C11.0566 1.96953 10.0441 1.76562 9 1.76562C8.6502 1.76562 8.36719 1.48262 8.36719 1.13281C8.36719 0.783008 8.6502 0.5 9 0.5C10.2146 0.5 11.3941 0.737305 12.5033 1.20664C13.5756 1.66016 14.5371 2.31055 15.3633 3.13672C16.1895 3.96289 16.8381 4.92617 17.2916 5.99668C17.7609 7.10586 17.9982 8.28535 17.9982 9.5C17.9982 10.7146 17.7609 11.8941 17.2916 13.0033C16.8398 14.0756 16.1895 15.0371 15.3633 15.8633C14.5371 16.6895 13.5738 17.3381 12.5033 17.7916C11.3941 18.2627 10.2146 18.5 9 18.5Z"
                                    fill="white"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_88_6703">
                                    <rect
                                      width="18"
                                      height="18"
                                      fill="white"
                                      transform="translate(0 0.5)"
                                    />
                                  </clipPath>
                                </defs>
                              </svg>
                              <span class="sr-only">Loading...</span>
                            </span>
                          </div>
                        </div>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: consentText?.legalNotice,
                    }}
                    className="text-xs font-normal text-gray-1200 consent-anchor-tag"
                  />
                </section>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Dialog.Panel>
  );
};

const stepsConfig = [
  {
    Component: (handleNext, title, closeModal) => (
      <SingleEmailForm {...{ handleNext, title, closeModal }} />
    ),
  },
  {
    Component: (handleNext, title, closeModal, email, setIsSuccess) => (
      <DemoForm {...{ handleNext, title, closeModal, email, setIsSuccess }} />
    ),
  },
];

export const SuccessModal = ({ isSuccess, closeSuccess }) => {
  const locationHook = useLocation();
  const lidar = locationHook?.pathname == "/lidar/";
  return (
    <>
      <Transition appear show={isSuccess} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {}}
          preventOutsideClick
        >
          <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-lg max-w-xs transform overflow-hidden rounded-3.5xl bg-white sm:p-9 py-6 px-5 text-left align-middle shadow-xl transition-all">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={closeSuccess}
                    className="cursor-pointer flex justify-end mb-2.5"
                  >
                    <CloseModal />
                  </span>
                  <div className="flex flex-col items-center justify-between gap-y-3">
                    <SuccessIcon />
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold text-center sm:text-2xl tracking-tighter_1 text-purple-1100"
                    >
                      We got your request!
                    </Dialog.Title>
                  </div>
                  <div className="px-5 mt-3 sm:px-0">
                    <p className="text-xs font-medium text-center sm:text-base text-gray-1200 tracking-tighter_1">
                      {lidar
                        ? `Thanks for joining our waitlist. We're gradually rolling out access to our Physical AI suite and will reach out soon!`
                        : `Thank you for submitting the form. Someone from our team
                      will get in touch with you as soon as possible.`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeSuccess}
                    className="bg-purple-1500 hover:bg-purple-400 font-semibold text-white py-2.5 px-7 tracking-tighter_1 sm:text-lg text-base shadow-lg rounded-full w-full mx-auto sm:mt-5 mt-3.5"
                  >
                    Continue Browsing
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

/**
 * @summary This component renders the Modal in home page by clicking
 * "Book a Demo" button.
 * @param {function}  closeModal to close this modal
 * @param {boolean} isOpen handles the opening and closing of modal
 * @returns JSX.Element
 */
const DemoModal = ({
  isOpen,
  email,
  closeModal,
  hideSuccessModal = false,
  location,
  routerId = "231",
  title = "Schedule a demo call",
}) => {
  const [currentStep, setCurrentStep] = useState(email ? 2 : 1);
  const ActiveComponent = stepsConfig[email ? 1 : currentStep - 1]?.Component;
  const locationHook = useLocation();
  const [isSuccess, setIsSuccess] = useState(false);
  const lidar = locationHook?.pathname === "/lidar/";
  const handleLoad = () => {
    if (window.RevenueHero) {
      window.hero = new RevenueHero({ routerId });
    }
  };

  const handleNext = () => {
    setCurrentStep((prevStep) => {
      if (prevStep === stepsConfig.length) {
        setIsSuccess(!isSuccess);
        return prevStep;
      } else {
        return prevStep + 1;
      }
    });
  };

  useEffect(() => {
    if (email) {
      setCurrentStep(2);
    }
  }, [email]);

  const handleClose = () => {
    localStorage.removeItem("workEmail");
    setCurrentStep(1);
    closeModal();
  };

  const closeSuccess = () => {
    setIsSuccess(false);
    setCurrentStep(1);
  };

  useEffect(() => {
    if (isOpen || routerId) handleLoad();
  }, [isOpen, routerId]);

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={() => {}}>
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 ">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="flex flex-col justify-center lg:flex-row rounded-[20px] demo_container w-full box_alignment all items-center lg:items-stretch">
                  {ActiveComponent(
                    handleNext,
                    title,
                    closeModal,
                    email,
                    handleLoad,
                    setIsSuccess
                  )}
                  <Dialog.Panel className="relative flex flex-col justify-center w-full max-w-xl space-y-[42px] transform overflow-hidden rounded-b-[20px] lg:rounded-b-[0px] lg:!rounded-r-[20px] detail_section px-9 lg:px-16 py-0 lg:py-10">
                    <img
                      src={bg}
                      alt="bg_vector"
                      className=" absolute inset-0 top-16 -right-16 z-[-1]"
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleClose}
                      className="cursor-pointer absolute right-6 top-6 !mt-0 hidden lg:block"
                    >
                      <CloseModal />
                    </span>
                    <section className="flex flex-col justify-between sm:flex-row">
                      <section className="flex flex-col gap-1.5">
                        <h4 className="text-lg sm:text-xl font-manrope font-semibold tracking-[-0.9px] sm:tracking-[-1px] text-white">
                          Trusted by our customers
                        </h4>

                        <section className="flex items-center gap-2">
                          <p className="text-gray-2200 text-sm tracking-[-0.7px]">
                            G2 reviews
                          </p>

                          <svg
                            width="74"
                            height="13"
                            viewBox="0 0 74 13"
                            role="presentation"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M6.21306 0.287109L7.60798 4.58021L12.122 4.58021L8.47008 7.23349L9.865 11.5266L6.21306 8.87331L2.56111 11.5266L3.95603 7.23349L0.304089 4.58021L4.81814 4.58021L6.21306 0.287109Z"
                              fill="#F05940"
                            />
                            <path
                              d="M21.7453 0.287109L23.1402 4.58021L27.6543 4.58021L24.0023 7.23349L25.3972 11.5266L21.7453 8.87331L18.0933 11.5266L19.4883 7.23349L15.8363 4.58021L20.3504 4.58021L21.7453 0.287109Z"
                              fill="#F05940"
                            />
                            <path
                              d="M36.9661 0.287109L38.4308 4.58021L43.1705 4.58021L39.336 7.23349L40.8006 11.5266L36.9661 8.87331L33.1316 11.5266L34.5962 7.23349L30.7617 4.58021L35.5014 4.58021L36.9661 0.287109Z"
                              fill="#F05940"
                            />
                            <path
                              d="M52.1882 0.287109L53.5831 4.58021L58.0971 4.58021L54.4452 7.23349L55.8401 11.5266L52.1882 8.87331L48.5362 11.5266L49.9311 7.23349L46.2792 4.58021L50.7932 4.58021L52.1882 0.287109Z"
                              fill="#F05940"
                            />
                            <path
                              d="M67.7223 0.287109L69.1173 4.58021L73.6313 4.58021L69.9794 7.23349L71.3743 11.5266L67.7223 8.87331L64.0704 11.5266L65.4653 7.23349L61.8134 4.58021L66.3274 4.58021L67.7223 0.287109Z"
                              fill="#F05940"
                            />
                          </svg>
                        </section>
                      </section>

                      <section className="flex gap-2 mt-2.5 sm:mt-0">
                        <HighPerformerIcon />
                        <LeaderIcon />
                        <LeaderAmericasIcon />
                        <BestSupportIcon />
                      </section>
                    </section>

                    <section className="bg-gray-2800 rounded-xl">
                      <h6 className="text-sm sm:text-base leading-7 font-manrope text-gray-2500 font-medium tracking-[-0.7px] sm:tracking-[-0.4px] p-6">
                        "A lot of platforms prioritize speed over quality or
                        quality over speed. Encord speeds up annotation while
                        still allowing for strong quality control."
                      </h6>

                      <section className="px-[28px] pb-[18px] space-y-1.5">
                        <svg
                          width="114"
                          height="21"
                          viewBox="0 0 114 21"
                          role="presentation"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clip-path="url(#clip0_2809_39527)">
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M10.4595 2.69154C11.6496 2.69143 12.8326 2.50883 13.9678 2.15006L14.4084 2.01146C13.2097 1.30907 11.8468 0.938965 10.4591 0.938965C9.07133 0.938965 7.70846 1.30907 6.5098 2.01146L6.95122 2.15006C8.08694 2.50904 9.2698 2.69154 10.4595 2.69154ZM6.61522 3.23992L5.34322 2.83789C3.92783 4.05158 2.97866 5.72343 2.65951 7.56493L3.64008 8.47917C5.56401 10.2815 6.90022 12.6262 7.47322 15.2051L7.76208 16.4999C9.50151 17.1484 11.4149 17.1484 13.1544 16.4999L13.4458 15.2043C14.0189 12.6245 15.3561 10.2794 17.2815 8.47745L18.2621 7.56321C17.9432 5.72183 16.9943 4.04999 15.5792 2.83617L14.3064 3.2382C11.8039 4.0286 9.12024 4.0286 6.6178 3.2382L6.61522 3.23992ZM2.53351 9.00172L2.87208 9.31766C4.6323 10.9608 5.85345 13.1027 6.37351 15.4591L6.46951 15.8921C5.27534 15.1884 4.28492 14.1836 3.59637 12.9772C2.90547 11.7668 2.53924 10.3969 2.53351 9.00172ZM14.5455 15.4574L14.4521 15.8904C15.6455 15.1863 16.6353 14.1816 17.3235 12.9755C18.0139 11.7655 18.3801 10.3963 18.3864 9.00172L18.0478 9.31766C16.2872 10.9599 15.0657 13.1013 14.5455 15.4574ZM5.0338 2.7432C4.77138 2.6617 4.49847 2.61935 4.2238 2.61751C1.96351 2.61751 0.576653 5.56168 2.41522 7.32819C2.32502 7.79508 2.27455 8.26883 2.26437 8.74432L1.68151 8.19939C0.492653 7.09403 0.118938 5.47301 0.698367 3.96994C1.28122 2.4548 2.6698 1.476 4.22294 1.476C4.6258 1.47686 5.02437 1.54056 5.40837 1.66367L6.15494 1.89868C5.75808 2.14834 5.3818 2.4307 5.03208 2.7432H5.0338ZM20.2198 3.96994C19.6352 2.45567 18.2518 1.476 16.6969 1.476C16.2946 1.47729 15.8949 1.54029 15.5115 1.6628L14.7641 1.89782C15.1609 2.14747 15.5364 2.42984 15.8869 2.74234C16.1492 2.66141 16.4218 2.61837 16.6969 2.61665C18.9564 2.61665 20.3432 5.56082 18.5089 7.33077C18.5989 7.79822 18.6495 8.27256 18.6598 8.74776L19.2427 8.20283C20.4264 7.09489 20.8009 5.47387 20.2198 3.96994ZM7.83665 16.8313C8.16151 18.2337 9.30922 18.9362 10.4595 18.9362C11.6089 18.9362 12.7584 18.2337 13.0824 16.8313C13.525 16.6822 13.954 16.4949 14.3647 16.2718L14.1889 17.0595C13.7861 18.8647 12.2887 20.0777 10.4604 20.0777C8.63122 20.0777 7.13208 18.8638 6.73008 17.0595L6.55522 16.2718C6.9658 16.4956 7.39522 16.6824 7.83665 16.8313Z"
                              fill="#4C31FB"
                            />
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M31.0425 2.66309V4.42787H35.7662V17.062H37.7676V4.42873H42.4922V2.66395L31.0425 2.66309ZM56.4379 8.17523C56.4079 8.13219 56.3779 8.08915 56.347 8.04783C55.6468 7.09484 54.6559 6.61792 53.3753 6.61792C52.0536 6.61792 51.0233 7.10087 50.2828 8.06763C49.5431 9.03524 49.1728 10.336 49.1719 11.9717C49.1719 13.5944 49.5422 14.8892 50.2828 15.8551C51.0233 16.8218 52.0545 17.3056 53.3753 17.3056C54.6559 17.3056 55.6468 16.8296 56.347 15.8766C56.3848 15.8249 56.4216 15.7715 56.4568 15.7182V17.0637H58.2002V6.86069H56.4379V8.17523ZM55.7059 14.6791C55.2585 15.3351 54.6002 15.6631 53.7345 15.6622C52.8671 15.6622 52.2139 15.3342 51.7742 14.6791C51.3336 14.0231 51.1133 13.114 51.1133 11.9519C51.1133 10.7888 51.3336 9.87976 51.7742 9.22291C52.2148 8.56865 52.8688 8.2398 53.7362 8.2398C54.6028 8.2398 55.2593 8.56693 55.7059 9.22377C56.1533 9.87976 56.3762 10.7888 56.3762 11.9519C56.3762 13.114 56.1533 14.0231 55.7076 14.6791H55.7059ZM71.6831 3.85883H73.6031V6.86069H75.9456V8.52389H73.5833V14.2547C73.5833 15.1001 74.0128 15.3781 75.2025 15.3781H75.9456V17.0818H75.2188C72.8822 17.0818 71.6822 16.2941 71.6822 14.1919V8.52303H69.8393V6.86069H71.6822L71.6831 3.85883ZM84.7888 8.17523L84.7871 8.17179C84.7587 8.12994 84.7295 8.08861 84.6996 8.04783C83.9976 7.09484 83.0085 6.61792 81.7262 6.61792C80.4053 6.61792 79.3742 7.10087 78.6336 8.06763C77.8939 9.03524 77.5236 10.336 77.5219 11.9717C77.5219 13.5944 77.8922 14.8892 78.6336 15.8551C79.3742 16.8218 80.4045 17.3056 81.7253 17.3056C83.0059 17.3056 83.9968 16.8296 84.6979 15.8766C84.7356 15.8249 84.7716 15.7715 84.8076 15.7182V17.0637H86.5493V6.86069H84.7879L84.7888 8.17523ZM84.0585 14.6791C83.6111 15.3351 82.9545 15.6631 82.0871 15.6622C81.2196 15.6622 80.5656 15.3342 80.1251 14.6791C79.6845 14.0231 79.4642 13.114 79.4651 11.9519C79.4651 10.7888 79.6845 9.87976 80.1251 9.22291C80.5665 8.56865 81.2205 8.2398 82.0871 8.2398C82.9528 8.2398 83.6111 8.56693 84.0585 9.22377C84.5059 9.87976 84.7296 10.7888 84.7296 11.9519C84.7296 13.114 84.5068 14.0231 84.0585 14.6791ZM91.3339 7.98154C92.0316 7.07246 93.0053 6.61792 94.2568 6.61792C95.5768 6.61792 96.6079 7.10087 97.3485 8.06763C98.0891 9.03438 98.4593 10.3291 98.4593 11.951C98.4585 13.5867 98.0882 14.8883 97.3485 15.8551C96.6079 16.8218 95.5776 17.3056 94.2559 17.3056C92.9753 17.3056 91.9845 16.8287 91.2842 15.8757C91.2461 15.8241 91.2092 15.7716 91.1736 15.7182V17.062H89.4319V2.66395H91.3339V7.98154ZM93.9156 15.6622C94.7693 15.6622 95.4165 15.3351 95.8579 14.6791C96.2976 14.0231 96.5188 13.114 96.5188 11.9519C96.5188 10.7888 96.2985 9.8789 95.8579 9.22291C95.4165 8.56779 94.7693 8.2398 93.9156 8.2398C93.0353 8.2398 92.3719 8.56693 91.9245 9.22377C91.4771 9.87976 91.2533 10.7888 91.2533 11.951C91.2542 13.114 91.4779 14.0231 91.9245 14.6791C92.3702 15.3342 93.0345 15.6622 93.9156 15.6622ZM102.511 2.66309H100.589V17.062H102.511V2.66309ZM44.4936 6.86069V8.61256C45.1982 7.3092 46.4685 6.59726 48.1159 6.59812H48.3559V8.54541H47.8648C45.6353 8.54541 44.6128 9.47687 44.6128 11.8236V17.062H42.6928V6.86069H44.4936ZM66.4348 15.125C66.0465 15.4832 65.5262 15.6622 64.8731 15.6622C63.3088 15.6622 62.2622 14.4914 62.2622 11.9501C62.2622 9.48203 63.2428 8.23894 64.8748 8.23894C65.5288 8.23894 66.0491 8.418 66.4365 8.77612C66.8231 9.13424 67.0699 9.61805 67.1753 10.2258L69.0113 9.88234C68.8039 8.93538 68.3822 8.17093 67.7453 7.58984C67.0322 6.94075 66.0739 6.6162 64.8739 6.61706C63.4193 6.61706 62.2982 7.09915 61.5105 8.06677C60.7219 9.03352 60.3285 10.3283 60.3293 11.951C60.3293 13.5867 60.7236 14.8883 61.5105 15.8551C62.2973 16.8218 63.4185 17.3056 64.8739 17.3056C66.0739 17.3056 67.0322 16.9785 67.7453 16.3216C68.3839 15.7363 68.8056 14.9684 69.0113 14.0197L67.1753 13.6753C67.0682 14.2831 66.8213 14.7669 66.4348 15.125ZM111.556 7.26702C112.229 7.69917 112.74 8.32761 113.087 9.15232C113.434 9.97703 113.595 10.9705 113.567 12.1335V12.4581H106.472C106.594 14.6154 107.648 15.7251 109.317 15.7251C110.623 15.7251 111.328 15.119 111.831 14.0205L113.507 14.4699C112.86 16.097 111.337 17.3056 109.336 17.3056C106.438 17.3056 104.64 15.3945 104.64 11.951C104.64 10.8706 104.813 9.9271 105.16 9.12219C105.508 8.31814 106.018 7.69917 106.692 7.26702C107.366 6.834 108.183 6.61792 109.144 6.61792C110.079 6.61792 110.883 6.834 111.556 7.26702ZM109.105 8.13908C107.44 8.13908 106.691 9.48203 106.516 10.9774H111.774C111.509 9.21689 110.785 8.13908 109.105 8.13908Z"
                              fill="black"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_2809_39527">
                              <rect
                                width="114"
                                height="19.8"
                                fill="white"
                                transform="translate(0 0.5)"
                              />
                            </clipPath>
                          </defs>
                        </svg>

                        <p className=" text-xs tracking-[-0.3px] text-gray-600">
                          Encord customer
                        </p>
                      </section>
                    </section>

                    <section className="max-w-[470px] overflow-hidden">
                      <LogoMarquee
                        logoArray={logo}
                        logoBackground=""
                        duration={20000}
                      />
                    </section>
                  </Dialog.Panel>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      {!hideSuccessModal && (
        <SuccessModal isSuccess={isSuccess} closeSuccess={closeSuccess} />
      )}
    </>
  );
};

export default DemoModal;
